import * as React from 'react';
import styled, { useTheme } from 'styled-components';

import { Input } from '@longline/aqua-ui/inputs/Input';
import { Slider } from '@longline/aqua-ui/inputs/Slider';
import { NumberFormatter } from '@longline/aqua-ui/formatters/NumberFormatter';
import { Form } from '@longline/aqua-ui/containers/Form';
import { InfoBox } from '@longline/aqua-ui/containers/InfoBox';
import { Validation } from '@longline/aqua-ui/containers/Form/Validation';
import { Icon } from '@longline/aqua-ui/controls/Icon';

import { Sprites } from '../../../svg';
import { AreaGridUtils } from '../../../util/AreaGridUtils';
import { IArea } from '../../../types/IArea';
import { useGlobalState } from '../../../contexts/state/GlobalStateContext';
import { Grid } from '../../../ui/Grid';
import { BathymetryFormatControl } from '../../../ui/inputs/BathymetryFormatControl';

interface IProps {
  value?: IArea;
}

const AreaTab = (props: IProps) => {
  const globalState = useGlobalState();
  const theme = useTheme();
  const area = props.value;

  // The field holds what the user typed rather than the size actually achieved.
  // Cell size is derived from an integer cell count, so the two differ by up to
  // half a cell, and echoing the achieved size back on every keystroke would
  // make the box impossible to type into. The rectangle moving is the one case
  // that must overwrite it, so that — and not area.resolution — is what this is
  // keyed on. It sits above the early return because hooks have to run on every
  // render, including those where <Tabs> has no area yet.
  const [cellSizeText, setCellSizeText] = React.useState('');
  React.useEffect(() => {
    if(!area) return;
    setCellSizeText(formatCellSize(AreaGridUtils.cellWidth(area.rect, area.resolution)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [area, area?.rect.minLng, area?.rect.maxLng, area?.rect.minLat, area?.rect.maxLat]);

  // Optional because the parent <Tabs> may render before an area is selected.
  if (!area) return null;

  const handleUpdate = (field: string, value: any) => {
    (area as any)[field] = value;
    globalState.updateArea(area);
  }

  const humanArea = (name: string, aream2: number) => {
    if(aream2 > 1000000) return <InfoBox.Row name={<>{name} <Unit>(km2)</Unit></>} value={<NumberFormatter value={aream2/1000000} decimals={2}/>}/>;
    return <InfoBox.Row name={<>{name} <Unit>(m2)</Unit></>} value={<NumberFormatter value={aream2} decimals={0}/>}/>;
  }

  const humanLength = (name: string, lengthMeters: number) => {
    if(lengthMeters > 1000) return <InfoBox.Row name={<>{name} <Unit>(km)</Unit></>} value={<NumberFormatter value={lengthMeters/1000} decimals={2}/>}/>;
    return <InfoBox.Row name={<>{name} <Unit>(m)</Unit></>} value={<NumberFormatter value={lengthMeters} decimals={0}/>}/>;
  }

  const getWidth = () => AreaGridUtils.width(area.rect);
  const getHeight = () => AreaGridUtils.height(area.rect);
  const getArea = () => getWidth() * getHeight();

  const verticalResolution = AreaGridUtils.verticalResolution(area.rect, area.resolution);
  const cellWidth = AreaGridUtils.cellWidth(area.rect, area.resolution);

  // A typed size the rectangle cannot honour is flagged in the hint, which is
  // too narrow to spell it out in words.
  const request = AreaGridUtils.resolutionForCellSize(area.rect, parseFloat(cellSizeText));

  const handleChangeCellSize = (value: string) => {
    setCellSizeText(value);
    if(Validation.getValidation(value, { required: {}, isFloat: {}, min: { value: 0 } }) !== null) return;
    handleUpdate("resolution", AreaGridUtils.resolutionForCellSize(area.rect, parseFloat(value)).resolution);
  }

  return (
    <>
      {humanLength("Width", getWidth())}
      {humanLength("Height", getHeight())}
      {humanArea("Area", getArea())} {/* Not enough space available for this. */}

      <Spacer/>

      <Grid>
        <Grid.Item weight={6}>
          <Form.Field
            label="Name" 
            name="name"
            hint="Name of this simulation area."
            wrapper='box'
            value={area.name}
            onChange={(v: string) => v.length > 1 ? handleUpdate("name", v) : {}}
            weight={1}
            rules={{ required: {} }}
            control={<Input/>}
          />      
        </Grid.Item>
        <Grid.Item weight={6}>
          <Form.Field
            label="Bathymetry format"
            name="negativeBathymetry"
            hint="Sign of depth values in the data."
            wrapper='box'
            value={area.bathymetry}
            onChange={(v: any) => handleUpdate("bathymetry", v)}
            weight={1}
            rules={{ required: {} }}
            control={<BathymetryFormatControl/>}
          />
        </Grid.Item>
        <Grid.Item weight={6}>
          <Form.Field
            label="Land detection" 
            name="land-detection"
            hint="Land detection threshold (cm)"
            wrapper='box'
            value={area.demThreshold * 100}
            onChange={(v: any) => handleUpdate("demThreshold", v / 100)}
            weight={1}
            rules={{ required: {} }}
            control={<Slider padded min={0} max={1000} ticks={101} hideTicks snapToTicks/>}
          />           
        </Grid.Item>
        <Grid.Item weight={6}>
          <Form.Field
            label="Cell size" 
            name="cell_size"
            hint={<>
              {request.clamped && <><Icon url={Sprites.Icons.Warning} size="small" color={theme.colors.negative}/> </>}
              <Bold><NumberFormatter value={area.resolution} decimals={0}/></Bold> <>&#10799; </>
              <Bold><NumberFormatter value={verticalResolution} decimals={0}/></Bold> = <Bold><NumberFormatter value={area.resolution * verticalResolution} decimals={0}/></Bold> cells
              = <Bold><NumberFormatter value={cellWidth} decimals={cellWidth >= 100 ? 0 : 1}/> m</Bold>
            </>}
            wrapper='box'
            value={cellSizeText}
            onChange={handleChangeCellSize}
            weight={1}
            rules={{ required: {}, isFloat: {}, min: { value: 0 } }}
            control={<Input unit="m"/>}
          />      
        </Grid.Item>
      </Grid>
    </>
  )
}

/** Metres, to one decimal until the numbers get large enough not to need it. */
const formatCellSize = (meters: number) => meters >= 100 ? meters.toFixed(0) : meters.toFixed(1);

const Bold = styled.span`
  font-weight: 700;
`

const Unit = styled.span`
  display: inline-block;
  font-size: 80%;
  font-weight: normal;
  color: #eee;
`

const Spacer = styled.div`
  height: 8px;
`

export { AreaTab }
